import { isValidWellSkyAgencyId, normalizeWellSkyAgencyId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { WellSkyDedicatedUserNote } from "./wellsky-dedicated-user-note";

interface Props {
    initialAgencyId: string;
    initialAppName: string;
    initialUsername: string;
    onSaved: () => void;
    onCancel: () => void;
    onError: (message: string) => void;
}

export const WellSkyApiCredentialForm: React.FC<Props> = props => {
    const {
        initialAgencyId,
        initialAppName,
        initialUsername,
        onSaved,
        onCancel,
        onError,
    } = props;
    const trpcClient = trpc.useTRPC();

    const [agencyId, setAgencyId] = React.useState(initialAgencyId);
    const [agencyIdTouched, setAgencyIdTouched] = React.useState(false);
    const [appName, setAppName] = React.useState(initialAppName);
    const [clientId, setClientId] = React.useState("");
    const [clientSecret, setClientSecret] = React.useState("");
    const [username, setUsername] = React.useState(initialUsername);
    const [password, setPassword] = React.useState("");

    const storeMutation = useMutation({
        ...trpcClient.wellSkyConnect.storeCredential.mutationOptions(),
        onSuccess: onSaved,
        onError: error => onError(error.message || "Failed to save credentials"),
    });

    const handleAgencyIdBlur = React.useCallback(() => {
        setAgencyId(current => normalizeWellSkyAgencyId(current));
        setAgencyIdTouched(true);
    }, []);

    const handleSave = React.useCallback(() => {
        storeMutation.mutate({
            agencyId: normalizeWellSkyAgencyId(agencyId),
            appName,
            clientId,
            clientSecret,
            username,
            password,
        });
    }, [agencyId, appName, clientId, clientSecret, username, password, storeMutation]);

    const agencyIdIsValid = isValidWellSkyAgencyId(agencyId);
    const showAgencyIdError =
        agencyIdTouched && agencyId.trim() !== "" && !agencyIdIsValid;

    return (
        <div className="space-y-4">
            <div>
                <Input
                    label="Agency ID"
                    value={agencyId}
                    onChange={e => setAgencyId(e.target.value)}
                    onBlur={handleAgencyIdBlur}
                    placeholder="1669"
                    aria-invalid={showAgencyIdError}
                />
                {showAgencyIdError && (
                    <p className="mt-1 text-sm text-red-600">
                        Enter the agency number, such as 1669.
                    </p>
                )}
            </div>
            <Input
                label="App name"
                value={appName}
                onChange={e => setAppName(e.target.value)}
                placeholder="The app name you gave WellSky"
            />
            <Input
                label="Client ID"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                placeholder="Enter your client ID"
            />
            <Input
                label="Client secret"
                type="password"
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder="Enter your client secret"
            />
            <div className="space-y-4 border-t border-gray-200 pt-4">
                <WellSkyDedicatedUserNote />
                <Input
                    label="Username"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter the WellSky username"
                />
                <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter the WellSky password"
                />
            </div>
            <div className="flex justify-end space-x-2">
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <PrimaryButton
                    onClick={handleSave}
                    disabled={
                        !agencyIdIsValid ||
                        !appName ||
                        !clientId ||
                        !clientSecret ||
                        !username ||
                        !password ||
                        storeMutation.isPending
                    }>
                    {storeMutation.isPending && <Spinner />}
                    <span className={storeMutation.isPending ? "ml-2" : ""}>
                        {storeMutation.isPending ? "Verifying..." : "Save"}
                    </span>
                </PrimaryButton>
            </div>
        </div>
    );
};
